import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { useGuestIdentity } from "../lib/guestIdentity";
import { formatApiError } from "../lib/api";
import { syncGuestProfileAfterSignIn } from "../lib/syncGuestProfile";
import { completeOAuthIfPresent, consumeOAuthReturnTo, formatAuthError } from "../lib/supabaseAuth";
import { ReadableNavButton } from "../components/Sticker";

const SUPPORT_EMAIL = "flipflopapps@gmail.com";

export default function AuthCallbackPage() {
  const nav = useNavigate();
  const { signIn } = useAuth();
  const { bindDancerId, refreshProfile } = useGuestIdentity();
  const [error, setError] = useState(null);
  const [retryTo, setRetryTo] = useState("/login");

  useEffect(() => {
    let cancelled = false;

    async function run() {
      const returnTo = consumeOAuthReturnTo();
      const isPlatformAdmin =
        returnTo === "/app-admin"
        || returnTo.startsWith("/app-admin?")
        || returnTo.startsWith("/switch-user");
      const loginHref = isPlatformAdmin
        ? returnTo.startsWith("/switch-user") ? "/switch-user" : "/app-admin"
        : `/login?next=${encodeURIComponent(returnTo)}`;

      if (!cancelled) setRetryTo(loginHref);

      try {
        if (isPlatformAdmin) {
          const result = await completeOAuthIfPresent({ exchange: false });
          if (!result?.session) {
            throw new Error("Sign-in did not complete. Try again.");
          }
          if (!cancelled) nav(returnTo, { replace: true });
          return;
        }

        const result = await completeOAuthIfPresent({ exchange: true });
        if (!result?.data) {
          throw new Error("Sign-in did not complete. Try again.");
        }

        const data = result.data;
        signIn({ token: data.token, account: data.organizer });

        try {
          const linked = await syncGuestProfileAfterSignIn(data.organizer);
          if (linked.dancerId) {
            bindDancerId(linked.dancerId);
            await refreshProfile();
          }
        } catch {
          /* profile sync is best-effort */
        }

        if (data.new_account) {
          toast.success("Welcome aboard!");
        } else {
          toast.success("Welcome back 💃");
        }

        if (!cancelled) {
          // First-time accounts start host onboarding; deep links for returning users stay intact.
          if (data.new_account && !data.organizer?.onboarding_completed_at) {
            nav("/onboarding", { replace: true });
          } else {
            nav(returnTo, { replace: true });
          }
        }
      } catch (err) {
        if (!cancelled) {
          const msg = err?.response ? formatApiError(err) : formatAuthError(err);
          setError(msg);
          toast.error(msg);
        }
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [nav, signIn, bindDancerId, refreshProfile]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-canvas font-body text-ink px-4">
      <div className="text-center space-y-4 max-w-sm w-full">
        <p className="font-ui font-semibold text-[20px] leading-tight text-balance">
          {error ? "Sign-in failed" : "Finishing sign-in…"}
        </p>
        {error ? (
          <>
            <p className="font-ui font-medium text-[14px] text-ink/80 leading-relaxed text-pretty">
              {error}
            </p>
            <p className="font-ui font-medium text-[13px] text-ink/75 leading-relaxed text-pretty">
              Go back and try again. If it keeps happening, email{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="text-plum font-semibold underline underline-offset-2 rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2"
                data-testid="auth-callback-support-email"
              >
                {SUPPORT_EMAIL}
              </a>
              .
            </p>
            <div className="pt-1 flex justify-center">
              <ReadableNavButton as={Link} to={retryTo} data-testid="auth-callback-back">
                <ArrowLeft className="w-4 h-4" strokeWidth={2.5} aria-hidden />
                Back to sign-in
              </ReadableNavButton>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
